/**
 * Copyright Highway9 Networks Inc.
 *
 * Per–Radio-Site Channel Pool configuration. Mirrors the backend YAML
 * defined in the "Frequency Configuration at Radio Site Level" spec.
 *
 * Owned by the Radio Site; implicitly inherited by all Radio Groups
 * and Radios under the site. Not exposed for override at lower levels.
 */

export type ChannelPoolBand = "B48" | "N48" | "N77" | "N78";

export type ChannelPoolBandwidth = 10 | 20 | 30 | 40 | 50 | 60 | 70 | 80 | 90 | 100;

/**
 * Channel "assignment group". Length determines mode:
 *  - 1 element  → single carrier
 *  - 2 elements → CA pair (primary + secondary)
 */
export type ChannelAssignmentGroup = (number | string)[];

export type ChannelRadioConfig = {
  band: ChannelPoolBand;
  bandwidth: ChannelPoolBandwidth | number | string;
  channelGroups: ChannelAssignmentGroup[];
};

export type ChannelPoolItem = {
  borderRadioConfig: ChannelRadioConfig;
  nonBorderRadioConfig: ChannelRadioConfig;
};

export type ChannelPciRange = {
  start: number | string | undefined;
  end: number | string | undefined;
};

export type SiteChannelPool = {
  channelPool: ChannelPoolItem[];
  borderRadioPciRange: ChannelPciRange;
  nonBorderRadioPciRange: ChannelPciRange;
};

export type ChannelBlockKind = "borderRadioConfig" | "nonBorderRadioConfig";

/**
 * Channel Allocation Strategy — chosen per Radio Site (4G). Defaults to the
 * system setting `Default.Channel.Allocation.Strategy` when a new site is
 * created; existing 4G sites get a value populated by the upgrade script.
 */
export type ChannelAllocationStrategy =
  | "CA_CHANNEL_ALLOCATION_STRATEGY_STATIC_PAIRS"
  | "CA_CHANNEL_ALLOCATION_STRATEGY_SHARED_SECONDARY";

export const CHANNEL_ALLOCATION_STRATEGY_STATIC_PAIRS: ChannelAllocationStrategy =
  "CA_CHANNEL_ALLOCATION_STRATEGY_STATIC_PAIRS";

export const CHANNEL_ALLOCATION_STRATEGY_SHARED_SECONDARY: ChannelAllocationStrategy =
  "CA_CHANNEL_ALLOCATION_STRATEGY_SHARED_SECONDARY";
